/*
 * Description:
 * ---------------------
 * Every sentence the Investigations and Outside Agents screens show
 * (InvestigationsPanel, OutsideAgentsPanel), as pure functions of what
 * notabox-server answers at /api/v1/mesh/* and /api/v1/mcp/keys
 * (server/pkg/meshctl, server/pkg/mcpgw). A task's findings are the server's
 * own sentences; these words say who asked what of which box, and whether
 * each answer's signature checked. They never decide anything.
 *
 * ! file components/meshWords.js
 */

export const NOT_ON_SERVER = "This Weavian server does not run investigations yet.";
export const NO_TASKS = "No investigations yet. Check a site above and the answer appears here.";
export const NO_SITES = "None of your sites can be asked yet.";

export const LEAD =
  "Ask a site's box, and the Weavian gateway it reaches the internet through, how the site is doing. " +
  "Each answer is signed by the box or gateway that gave it, and the whole investigation by this console, " +
  "so anyone can check it later. Investigations only read: nothing on a box changes.";

/* What a read asks a box for, as the trace names it; and what a box asks its
 * gateway, from the gateway's side. */
const ASKS = {
  wan_links: "for its internet lines",
  wan_health: "how each of its lines reaches its Weavian gateway",
  "anchor.session": "how its Weavian gateway hears its lines",
  "anchor.load": "how busy its Weavian gateway is",
  "anchor.flow": "what its Weavian gateway holds of the connection now",
  flow_stalls: "for its latest interrupted connections",
  flow_stall: "for one interrupted connection",
  changes: "for its signed record of changes",
  class_rules: "for the order of lines set on each kind of traffic",
  class_wan_stats: "how much of each kind of traffic each of its lines carried",
  wan_capacity: "how much each of its lines can carry",
  policy: "how each kind of traffic uses its lines",
  bond_weights: "how it shares traffic across its lines",
  "class.reroute": "to set the order of lines one kind of traffic uses",
  "class.action": "to set how one kind of traffic uses the lines",
  "line.labels": "to rename one of its lines",
  "class.encryption": "to set how one kind of traffic is sealed",
};
const GATEWAY_ASKS = {
  "anchor.session": "how it hears the box's lines",
  "anchor.load": "how busy it is",
  "anchor.flow": "what it holds of the connection now",
};

/* Who an investigation is for: a person by name, an outside agent by the
 * name its key was given. */
export const by = (b) => (b?.kind === "agent" ? `${b.name}, an outside agent` : b?.name || "someone");

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

export const status = (t) => ({
  running: { text: "Asking now", tone: "busy" },
  done: { text: "Answered", tone: "ok" },
  interrupted: { text: "Stopped when the server restarted", tone: "bad" },
}[t.status] || { text: t.status, tone: "" });

export const when = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString([], { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};

export const took = (t) => {
  if (!t.ended) return "";
  const ms = new Date(t.ended) - new Date(t.started);
  return ms >= 0 ? `${(ms / 1000).toFixed(1)} s` : "";
};

/* One read in the trace, as a sentence: who asked whom for what. A read the
 * console made names the box; one a box made names its gateway. A read the
 * console refused itself, since the investigation does not declare it, was
 * never asked. */
export const hopAsked = (hop, console) => {
  if (hop.from !== console) return `${hop.from} asked its Weavian gateway ${GATEWAY_ASKS[hop.capability] || `for ${hop.capability}`}.`;
  const what = ASKS[hop.capability] || `for ${hop.capability}`;
  return hop.refusal?.check === "undeclared" ? `This console did not ask ${hop.node} ${what}.` : `This console asked ${hop.node} ${what}.`;
};

/* Why a node refused a question, by the check that refused it. */
const REFUSED = {
  signature: "it does not hold the key of whoever asked",
  call: "the question was not one it takes",
  transport: "the question came by a path it does not answer",
  replay: "it had been asked the same question before",
  capability: "it does not answer that",
  version: "the question was written for another version of what it answers",
  args: "the question's details were not ones it takes",
  idempotency: "the same request was already in hand",
  breaker: "too many questions from whoever asked were refused lately",
  budget: "whoever asked has asked as much as it may for now",
  rules: "its rules do not allow it",
  undeclared: "this investigation may not ask for that, so this console did not ask",
  mandate: "whoever asked carried no key card that covers it",
  held: "another change is on trial there",
  before: "the setting changed since whoever asked read it",
  undo: "it could not be taken back there",
  cooldown: "a change was taken back there a short while ago",
  flap: "it would undo the change before it to the same setting",
  wave: "its key card names a rollout the box does not take",
  probes: "the box cannot read its lines, so it could not judge the change",
};

/* A wait, in the unit a person reads it in. */
const waitWords = (s) => (s < 120 ? plural(s, "second", "seconds")
  : s < 7200 ? plural(Math.ceil(s / 60), "minute", "minutes")
    : plural(Math.ceil(s / 3600), "hour", "hours"));

/* What a refusal says of asking again; a node built before it said so says
 * nothing. */
const again = (r) => ({
  never: " Asking again the same way would be refused too.",
  now: " Asking again at once can work.",
  later: ` It can be asked again in ${waitWords(r?.retry_after_s || 1)}.`,
}[r?.retry] || "");

/* What came of it, and whether its signatures checked: a sentence, and the
 * node's or the check's own reason beside it. Only a checked answer is called
 * signed. */
export const hopAnswer = (hop, console) => {
  const signer = hop.from === console ? hop.node : "the gateway";
  const secs = hop.took_ms > 0 ? ` in ${(hop.took_ms / 1000).toFixed(1)} s` : "";
  switch (hop.status) {
    case "ok":
      if (hop.checked) {
        return { text: `Answered, but the answer's signature did not check against the key this console holds for ${signer}.`, why: hop.checked, tone: "bad" };
      }
      return hop.repeats
        ? { text: `Answered${secs} as the same request was before, signed by ${signer} and checked. Nothing was done again.`, tone: "ok" }
        : { text: `Answered${secs}, signed by ${signer} and checked.`, tone: "ok" };
    case "refused":
      return { text: `Refused: ${REFUSED[hop.refusal?.check] || "it would not answer"}.${again(hop.refusal)}`, why: hop.refusal?.reason, tone: "bad" };
    case "failed":
      return { text: "Could not answer.", why: hop.error, tone: "bad" };
    default:
      return { text: "No answer came back.", why: hop.error || hop.checked, tone: "bad" };
  }
};

/* How many of a task's reads came back signed and checked. */
export const checkedCount = (hops) => {
  let all = 0;
  let good = 0;
  const walk = (list) => (list || []).forEach((h) => {
    all += 1;
    if (h.status === "ok" && !h.checked) good += 1;
    walk(h.hops);
  });
  walk(hops);
  return { all, good };
};

export const HOW_WE_KNOW = "How we know";
export const howWeKnow = ({ all, good }) => (all === 0
  ? "No box was asked."
  : good === all
    ? `${plural(all, "answer", "answers")}, every one signed by the box or gateway that gave it and checked against the key this console holds for it.`
    : `${good} of ${plural(all, "answer", "answers")} signed and checked; the rest say why not below.`);

export const SIGNED_RECORD = "Download the signed record";
export const signedRecord = (ctl) => (ctl
  ? `The record of this investigation is signed by this console (${ctl.node}, key ${ctl.digest.slice(0, 16)}). ` +
    "Anyone can check it, and every answer in it, with Weavian's checker."
  : "");
export const NOT_SEALED = "The signed record is made once every answer is in.";

export const ASK = "Ask about a site";
export const CHECK_SITE = "Check this site";
export const CHECK_CHANGES = "What changed here";
export const CHECK_ALL = "Check every site";
export const LOOK_INTO = "Look into this interruption";
export const RECENT = "Recent investigations";

/* Outside agents. */
export const AGENTS_LEAD =
  "Let an AI agent outside Weavian, such as AWS DevOps Agent, read how your sites reach the internet. " +
  "It can only read, it sees only the customer its key is for, and every question it asks appears on " +
  "the Investigations screen beside your own.";
export const NO_KEYS = "No agent has a key yet.";
export const MAKE_KEY = "Make a key";
export const KEY_SHOWN_ONCE =
  "Paste these into the agent's settings now. The key is shown only this once; if it is lost, make another and revoke this one.";
export const keyUse = (k) => (k.calls
  ? `Asked ${plural(k.calls, "question", "questions")}, the last on ${when(k.last_used)}.`
  : "Not used yet.");
export const askRevoke = (k) => ({
  title: `Revoke ${k.name}'s key?`,
  message: `${k.name} will no longer be able to read your sites. Investigations it already asked stay on the Investigations screen.`,
  confirmLabel: "Revoke",
});
export const AGENT_NAME = "Key for";
export const AGENT_NAME_HINT = "Who the key is for, as you want it shown, such as AWS DevOps Agent";

/* How long a key works, and what its agent may ask. A key made before either
 * existed works until revoked and may ask everything. */
export const WORKS_FOR = "Works for";
export const WORKS_FOR_CHOICES = [
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
  { days: 365, label: "1 year" },
];
export const WORKS_FOR_DEFAULT = 90;
export const MAY_ASK = "May ask";
// The keys table's columns, beside each value on a phone.
export const KEY_COLS = { agent: "Agent", customer: "Customer", made: "Made", works: "Works", ask: MAY_ASK, use: "Use" };
export const PICK_A_QUESTION = "Pick at least one thing the agent may ask.";

const day = (d) => d.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });

export const keyEnds = (k, now = new Date()) => {
  if (!k.expires) return { text: "Until revoked", tone: "" };
  const at = new Date(k.expires);
  return at <= now ? { text: `Stopped working on ${day(at)}`, tone: "bad" } : { text: `Until ${day(at)}`, tone: "" };
};

const titleOf = (name, offered) => offered?.find((t) => t.name === name)?.title || name;
export const keyTools = (k, offered) => (k.tools?.length ? k.tools.map((n) => titleOf(n, offered)).join(", ") : "Everything");

/* What a key just made allows, in one sentence beside it. */
export const madeGrant = (k, offered) => {
  const until = k.expires ? `It works until ${day(new Date(k.expires))}` : "It works until revoked";
  const asks = k.tools?.length ? `may ask only: ${k.tools.map((n) => titleOf(n, offered)).join(", ")}` : "may ask everything";
  return `${until}, and its agent ${asks}.`;
};
